import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { OverlayProvider } from 'react-aria';
import { connectMenu, connectRefinementList, connectSortBy } from 'react-instantsearch-dom';

import { IconNavigationCheckmarkNormal, IconOperationArrowSortBy } from '../../../components/Icons';
import { PopoverTrigger } from '../../../components/PopoverTrigger/PopoverTrigger';
import { Select } from '../../../components/Select/Select';
import { TriggerIcon } from '../../../components/TriggerIcon/TriggerIcon';
import Autocomplete from './Autocomplete';
import * as css from './TutorialToolbar.module.scss';

const SortBy = ({ items, currentRefinement, refine, ...rest }) => {
  return (
    <OverlayProvider>
      <PopoverTrigger
        className="sort-by-popover"
        popoverPlacement={'bottom-end'}
        TriggerElement={
          <TriggerIcon tag="button" className="sort-by-icon">
            <IconOperationArrowSortBy />
          </TriggerIcon>
        }
        isDismissable={true}
      >
        <div className={css.sortWrapper}>
          <div className={css.sortLabel}>{'Sort by:'}</div>
          {items.map((i) => (
            <div className={css.sortEl} onClick={() => refine(i.value)} key={i.value} aria-label={'sort by ' + i.label}>
              {i.label}
              {currentRefinement === i.value && <IconNavigationCheckmarkNormal />}
            </div>
          ))}
        </div>
      </PopoverTrigger>
    </OverlayProvider>
  );
};

SortBy.propTypes = {
  items: PropTypes.array.isRequired,
  refine: PropTypes.func,
  currentRefinement: PropTypes.array,
};

const CustomSortBy = connectSortBy(SortBy);

const ProductRefinement = ({ items, refine, currentRefinement, products, ...rest }) => {
  const options = useMemo(
    () =>
      items
        .filter((i) => products.find((p) => p.value === i.label))
        .map(function (i) {
          return {
            label: products.find((p) => p.value === i.label)['name'],
            value: i.value.pop(),
          };
        }),
    [items, products]
  );

  return (
    <Select
      options={options}
      value={[currentRefinement]}
      isSearchable={true}
      isMulti={true}
      label={'Products'}
      className={css.products}
      withoutStatus={true}
      onChange={(option) => {
        if (option) {
          refine(option);
        } else {
          refine([]);
        }
      }}
      {...rest}
    />
  );
};

ProductRefinement.propTypes = {
  products: PropTypes.array,
  items: PropTypes.array.isRequired,
  refine: PropTypes.func,
  currentRefinement: PropTypes.array,
};

const CustomProductRefinement = connectRefinementList(ProductRefinement);

const DifficultySelect = (props) => {
  const { items, currentRefinement, defaultValue, refine, ...rest } = props;
  const difficultyOrder = ['easy', 'intermediate', 'advanced'];
  return (
    <Select
      options={[
        { label: 'All Difficulties', value: null },
        ...items.sort((a, b) => {
          return difficultyOrder.indexOf(a.label) - difficultyOrder.indexOf(b.label);
        }),
      ]}
      value={currentRefinement}
      isSearchable={false}
      withoutStatus={true}
      isMulti={false}
      placeholder={''}
      className={css.difficulty}
      onChange={(option) => {
        if (option) {
          refine(option);
        } else {
          refine(defaultValue);
        }
      }}
      {...rest}
    />
  );
};

DifficultySelect.propTypes = {
  items: PropTypes.array.isRequired,
  defaultValue: PropTypes.string,
  refine: PropTypes.func,
  currentRefinement: PropTypes.string,
};

const CustomDifficultyMenu = connectMenu(DifficultySelect);

const TutorialToolbar = ({ products, onSubmit, onReset, searchState }) => {
  return (
    <div className={css.toolbarWrapper}>
      <div className={css.toolbar}>
        <Autocomplete onSubmit={onSubmit} onReset={onReset} searchState={searchState} />
        <CustomProductRefinement attribute="product_slug" products={products} limit={100} />
        <CustomDifficultyMenu attribute="difficulty" />
        <CustomSortBy
          defaultRefinement={process.env.GATSBY_ALGOLIA_INDEX_NAME}
          items={[
            { value: process.env.GATSBY_ALGOLIA_INDEX_NAME, label: 'Relevance' },
            { value: process.env.GATSBY_ALGOLIA_INDEX_NAME + '_date_desc', label: 'Newest' },
            { value: process.env.GATSBY_ALGOLIA_INDEX_NAME + '_difficulty_asc', label: 'Difficulty' },
          ]}
        />
      </div>
    </div>
  );
};

TutorialToolbar.propTypes = {
  products: PropTypes.array.isRequired,
  children: PropTypes.node,
  onSubmit: PropTypes.func,
  onReset: PropTypes.func,
  searchState: PropTypes.object,
};

export default TutorialToolbar;
